import { useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { track, getFilterFromRules } from '@utils';
import { getUnderperformingAssets } from '@api/autoInsights';
import { createTempDashboard } from '@store/tempDashboard/actions';
import { DASHBOARD_TYPE, INTERNAL_UNKNOWN } from '@utils/constants';
import { AUTO_INSIGHTS_BROKENASSET_CLICK } from '@src/utils/sensor/track';
import { createFilterRule, ALL_TRAFFIC } from '@utils/filter';
import { allDimensionsListSelector } from '@store/common/dimensionList/selectors';
import { SettingTypes } from '../Settings';
import { metricShortMap, formatInterval, getBrokenAssetMetrics } from './formatter';
import { drawerEvent } from '../AlertInsights/hooks';
import {
  getInvertedPercentageMetricByKey,
  getSecondaryMetric,
} from '@src/utils/metrics/metricHelpers';

function useFetchSummary({ to, limit = 100 }) {
  return useQuery(
    [SettingTypes.brokenAssests, to, limit],
    () =>
      getUnderperformingAssets({
        params: {
          to,
          limit,
        },
      }),
    {
      // default cacheTime is 5 minutes
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
}

function useOpenSummary({ closeMore }) {
  const dispatch = useDispatch();
  const dimensionsList = useSelector(allDimensionsListSelector);
  const rules = [];

  return (values) => {
    const metric = values.rootcause[0];

    const interval = {
      from: formatInterval(values.interval.start),
      to: formatInterval(values.interval.end),
      shortcut: 'custom',
    };

    Object.entries(values.dimension).forEach(([key, value]) => {
      const dimensionObj = dimensionsList.find((d) => d.groupBy === key);

      // Unknown and NULL will not be added to filter list
      if (value === INTERNAL_UNKNOWN || value === 'NULL') {
        return;
      }
      if (key === 'OBJECT_NAME' && dimensionObj) {
        const assetRule = createFilterRule(dimensionObj, value, value);
        rules.push(assetRule);
      }
      if (key === 'channel' && dimensionObj) {
        const channelRule = createFilterRule(dimensionObj, value, value);
        rules.push(channelRule);
      }

      const contentDimensionObj = dimensionsList.find((d) => d.groupBy === 'c3.video.isLive');
      if (key === 'c3.video.isLive' && contentDimensionObj) {
        const contentRule = createFilterRule(contentDimensionObj, 'VoD', 'F');
        rules.push(contentRule);
      }
    });
    const filter = getFilterFromRules(rules, true);

    const selectedMetric = getInvertedPercentageMetricByKey({
      metricKey: metric,
      defaultValue: metric,
    });
    const restBrokenAssetMetrics = getBrokenAssetMetrics();
    const metricList = [selectedMetric];

    const secondaryMetric = getSecondaryMetric({ metricKey: metric });
    if (secondaryMetric) {
      metricList.push(secondaryMetric);
    }

    const dashboardConfig = {
      // usecase key enforces the new drill-down workflow (without injector layer)
      usecaseKey: 'ba-dashboard',
      metricsChartContainer: {
        metric: selectedMetric,
        metrics: [...new Set([...metricList, ...restBrokenAssetMetrics])],
      },
      common: {
        appliedFilters: [filter],
        interval,
      },
      'module:trends': {
        base: {
          compareFilters: [],
        },
      },
      toggles: {
        sections: {
          summary: false,
        },
      },
      metricPanel: {
        unit: '%',
        layout: '4l',
      },
      summary: {
        drilldownData: [ALL_TRAFFIC, filter],
      },
    };

    dispatch(
      createTempDashboard(
        DASHBOARD_TYPE.TRENDS_DRILLDOWN,
        dashboardConfig,
        `${metricShortMap[metric]} - BrokenAssets`,
        { newBrowserTab: true },
      ),
    );

    document.dispatchEvent(drawerEvent);
    closeMore();
    track({ event: AUTO_INSIGHTS_BROKENASSET_CLICK });
  };
}

export { useFetchSummary, useOpenSummary };
